import { VIDEO_EDITOR_TIMELINE_SUBDIVISION_SECONDS } from "./constants";

const FILMSTRIP_TILE_WIDTH = 96;
const FILMSTRIP_TILE_HEIGHT = 54;
const MAX_TILES = 10;
const SEEK_TIMEOUT_MS = 320;
const MAX_CACHED_FRAMES = 40;

const sharedVideos = new Map();
const videoTaskQueues = new Map();
const frameCache = new Map();

export const getFilmstripFrameTimes = ({
  inPoint = 0,
  outPoint = VIDEO_EDITOR_TIMELINE_SUBDIVISION_SECONDS,
  interval = VIDEO_EDITOR_TIMELINE_SUBDIVISION_SECONDS,
}) => {
  const duration = Math.max(outPoint - inPoint, 0.1);
  const rawCount = Math.ceil(duration / interval);
  const tileCount = Math.max(1, Math.min(MAX_TILES, rawCount));
  const step = duration / tileCount;

  return Array.from({ length: tileCount }, (_, index) => {
    const seekTime = inPoint + index * step;
    return Math.min(Math.max(seekTime, inPoint + 0.05), Math.max(outPoint - 0.05, inPoint + 0.05));
  });
};

const evictFrameCache = () => {
  while (frameCache.size > MAX_CACHED_FRAMES) {
    const oldestKey = frameCache.keys().next().value;
    const cached = frameCache.get(oldestKey);
    cached?.close?.();
    frameCache.delete(oldestKey);
  }
};

const getFrameCacheKey = ({ sourceUrl, seekTime }) =>
  `${sourceUrl}|${seekTime.toFixed(2)}`;

const loadSharedVideo = ({ sourceUrl }) => {
  if (sharedVideos.has(sourceUrl)) {
    return sharedVideos.get(sourceUrl);
  }

  const pending = new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.preload = "auto";
    video.setAttribute("playsinline", "true");

    const onReady = () => {
      if (!video.videoWidth || !video.duration) {
        return;
      }
      video.removeEventListener("loadeddata", onReady);
      video.removeEventListener("canplay", onReady);
      video.removeEventListener("error", onError);
      resolve(video);
    };

    const onError = () => {
      video.removeEventListener("loadeddata", onReady);
      video.removeEventListener("canplay", onReady);
      video.removeEventListener("error", onError);
      sharedVideos.delete(sourceUrl);
      reject(new Error("Video filmstrip load failed"));
    };

    video.addEventListener("loadeddata", onReady);
    video.addEventListener("canplay", onReady);
    video.addEventListener("error", onError);
    video.src = sourceUrl;
    video.load();
  });

  sharedVideos.set(sourceUrl, pending);
  return pending;
};

const waitForFrame = () =>
  new Promise((resolve) => {
    window.requestAnimationFrame(() => resolve());
  });

const seekVideo = ({ video, seekTime, signal }) =>
  new Promise((resolve) => {
    if (signal?.aborted) {
      resolve(false);
      return;
    }

    const target = Math.min(
      Math.max(seekTime, 0.05),
      Math.max((video.duration || seekTime) - 0.05, 0.05)
    );

    let settled = false;
    const finish = (didSeek) => {
      if (settled) return;
      settled = true;
      video.removeEventListener("seeked", onSeeked);
      signal?.removeEventListener?.("abort", onAbort);
      window.clearTimeout(timeoutId);
      resolve(didSeek);
    };

    const onSeeked = () => finish(true);
    const onAbort = () => finish(false);

    if (video.readyState >= 2 && Math.abs((video.currentTime || 0) - target) < 0.04) {
      finish(true);
      return;
    }

    video.addEventListener("seeked", onSeeked);
    signal?.addEventListener?.("abort", onAbort, { once: true });
    const timeoutId = window.setTimeout(() => finish(video.readyState >= 2), SEEK_TIMEOUT_MS);

    try {
      video.currentTime = target;
    } catch {
      finish(false);
    }
  });

const drawVideoCover = ({ ctx, video, dx, dy, dWidth, dHeight }) => {
  const videoWidth = video.videoWidth || dWidth;
  const videoHeight = video.videoHeight || dHeight;
  if (!videoWidth || !videoHeight) return false;

  const scale = Math.max(dWidth / videoWidth, dHeight / videoHeight);
  const sourceWidth = dWidth / scale;
  const sourceHeight = dHeight / scale;
  const sourceX = (videoWidth - sourceWidth) / 2;
  const sourceY = (videoHeight - sourceHeight) / 2;

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "medium";
  ctx.drawImage(
    video,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    dx,
    dy,
    dWidth,
    dHeight
  );
  return true;
};

const captureFrame = async ({ video, sourceUrl, seekTime, signal }) => {
  const cacheKey = getFrameCacheKey({ sourceUrl, seekTime });
  if (frameCache.has(cacheKey)) {
    return frameCache.get(cacheKey);
  }

  const didSeek = await seekVideo({ video, seekTime, signal });
  if (!didSeek || signal?.aborted || !video.videoWidth) {
    return null;
  }

  await waitForFrame();
  if (signal?.aborted) return null;

  const tile = document.createElement("canvas");
  tile.width = FILMSTRIP_TILE_WIDTH;
  tile.height = FILMSTRIP_TILE_HEIGHT;
  const tileCtx = tile.getContext("2d", { alpha: false });
  if (!tileCtx) return null;

  tileCtx.fillStyle = "#111827";
  tileCtx.fillRect(0, 0, tile.width, tile.height);
  const drew = drawVideoCover({
    ctx: tileCtx,
    video,
    dx: 0,
    dy: 0,
    dWidth: tile.width,
    dHeight: tile.height,
  });

  if (!drew) return null;

  frameCache.set(cacheKey, tile);
  evictFrameCache();
  return tile;
};

const runOnSharedVideoQueue = ({ sourceUrl, task }) => {
  const previous = videoTaskQueues.get(sourceUrl) || Promise.resolve();
  const next = previous.catch(() => {}).then(task);
  videoTaskQueues.set(sourceUrl, next);
  return next;
};

export const drawFilmstripToCanvas = ({
  canvas,
  sourceUrl,
  inPoint = 0,
  outPoint = 5,
  signal,
}) => {
  if (!canvas || !sourceUrl || signal?.aborted) {
    return Promise.resolve(0);
  }

  return runOnSharedVideoQueue({
    sourceUrl,
    task: async () => {
      if (signal?.aborted) return 0;

      const frameTimes = getFilmstripFrameTimes({ inPoint, outPoint });
      const ctx = canvas.getContext("2d", { alpha: false });
      if (!ctx) return 0;

      canvas.width = frameTimes.length * FILMSTRIP_TILE_WIDTH;
      canvas.height = FILMSTRIP_TILE_HEIGHT;
      ctx.fillStyle = "#111827";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      let video;
      try {
        video = await loadSharedVideo({ sourceUrl });
      } catch {
        return 0;
      }

      if (signal?.aborted) return 0;

      let drawnCount = 0;
      let failedSeeks = 0;

      for (let index = 0; index < frameTimes.length; index += 1) {
        if (signal?.aborted) {
          return drawnCount;
        }

        try {
          const tile = await captureFrame({
            video,
            sourceUrl,
            seekTime: frameTimes[index],
            signal,
          });

          if (signal?.aborted) {
            return drawnCount;
          }

          if (!tile) {
            failedSeeks += 1;
            if (failedSeeks >= 2 && drawnCount === 0) {
              return 0;
            }
            continue;
          }

          failedSeeks = 0;
          ctx.drawImage(tile, index * FILMSTRIP_TILE_WIDTH, 0);
          drawnCount += 1;

          if (index < frameTimes.length - 1) {
            ctx.fillStyle = "rgba(255,255,255,0.18)";
            ctx.fillRect(
              (index + 1) * FILMSTRIP_TILE_WIDTH - 1,
              0,
              1,
              FILMSTRIP_TILE_HEIGHT
            );
          }
        } catch {
          failedSeeks += 1;
          if (failedSeeks >= 2 && drawnCount === 0) {
            return 0;
          }
        }

        await waitForFrame();
      }

      return drawnCount;
    },
  });
};
